/**
 * @file PlanWorkspaceHeader.jsx
 * @module ui/src/components/tenant-admin/PlanWorkspaceHeader
 * @description
 *   Renders the persistent identity, temporal context, autosave state, validation
 *   indicators, and governance action entry points for the plan workspace.
 *
 * @version 1.0.1
 * @since S755
 * @author data-fit SME Build · Reviewed: Claude S755 · Corrected: Claude S756
 *
 * @architecture
 *   Locked decisions : #1, #2, #14, #15, #25
 *   Temporal invariants : INV-1, INV-3
 *   Design principles : DP-1, DP-2, DP-10, DP-14
 *   Cross-cutting contracts : none
 *
 * @domain-rules
 *   - Effective dates are always visible because temporal applicability is independent of workflow status.
 *   - planStatus controls action availability only and is never used to derive read-only or temporal behavior.
 *   - Autosave health and document validation are presented independently because document persistence is not governance approval.
 *   - The approved version remains visibly governing while an amendment draft is in flight.
 *   - Governance mutations are initiated through callbacks and never reflected optimistically in this component.
 *
 * @session-6-migration-flags
 *   - plan.effective_from — pending m_tc_01
 *   - plan.effective_to — pending m_tc_01
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group E batch delivery
 *   1.0.1  S756  OI-S755-C08-MENU-FOCUS-01 — added triggerRef on More▾ button;
 *                focus returned to trigger on menu close via Escape and outside
 *                mousedown. useRef was already imported but unused — now exercised
 *                for its intended accessibility purpose.
 */

import {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} PlanHeaderIdentity
 * @property {number} planId
 * @property {string} planCode
 * @property {string} planName
 * @property {number} version
 * @property {string} planStatus
 * @property {string} effectiveFrom
 * @property {string|null} effectiveTo
 */

/**
 * @typedef {Object} SaveState
 * @property {'idle'|'saving'|'saved'|'error'} status
 * @property {string|null} lastSavedAt
 * @property {string|null} errorMessage
 */

/**
 * @typedef {Object} ValidationState
 * @property {boolean} hasBlockingErrors
 * @property {number} blockingCount
 * @property {number} warningCount
 */

/**
 * @typedef {Object} AmendmentHeaderState
 * @property {boolean} inAmendmentMode
 * @property {number|null} draftVersion
 * @property {string|null} amendmentEffectiveDate
 */

/**
 * @typedef {Object} PlanWorkspaceHeaderProps
 * @property {PlanHeaderIdentity} plan
 * @property {SaveState} saveState
 * @property {ValidationState} validationState
 * @property {AmendmentHeaderState} amendmentState
 * @property {Object} currentUser
 * @property {boolean} canSubmit
 * @property {boolean} canProposeAmendment
 * @property {boolean} canCreateTemplate
 * @property {Function} onSave
 * @property {Function} onSubmit
 * @property {Function} onSaveAs
 * @property {Function} onSaveAsTemplate
 * @property {Function} onProposeAmendment
 * @property {Function} onCancelAmendment
 */

const styles = {
  header: {
    position: 'relative',
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    background: 'rgb(var(--color-surface))',
    borderBottom: '1px solid rgb(var(--color-border))',
    color: 'rgb(var(--color-text))',
    padding: '16px 20px',
  },
  primaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '20px',
    flexWrap: 'wrap',
  },
  identity: {
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  name: {
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  code: {
    color: 'rgb(var(--color-text-muted))',
  },
  badge: {
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '999px',
    padding: '2px 8px',
    background: 'rgb(var(--color-surface-muted))',
    fontSize: '0.8125rem',
  },
  temporal: {
    marginTop: '6px',
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.875rem',
  },
  statuses: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '10px',
    fontSize: '0.875rem',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    position: 'relative',
  },
  menu: {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 6px)',
    zIndex: 20,
    minWidth: '240px',
    padding: '6px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '6px',
    background: 'rgb(var(--color-surface))',
    boxShadow: '0 8px 20px rgb(var(--color-border))',
  },
  menuButton: {
    width: '100%',
    textAlign: 'left',
  },
  amendmentRow: {
    marginTop: '12px',
    paddingTop: '10px',
    borderTop: '1px solid rgb(var(--color-border))',
    fontWeight: 600,
  },
};

function formatRelativeSavedTime(lastSavedAt) {
  if (!lastSavedAt) {
    return 'recently';
  }

  const savedMs = new Date(lastSavedAt).getTime();
  const elapsedSeconds = Math.max(
    0,
    Math.round((Date.now() - savedMs) / 1000),
  );

  if (elapsedSeconds < 60) {
    return 'just now';
  }

  const elapsedMinutes = Math.round(elapsedSeconds / 60);

  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}m ago`;
  }

  const elapsedHours = Math.round(elapsedMinutes / 60);

  if (elapsedHours < 24) {
    return `${elapsedHours}h ago`;
  }

  return new Date(lastSavedAt).toLocaleString();
}

/**
 * Persistent plan workspace header.
 *
 * @param {PlanWorkspaceHeaderProps} props
 * @returns {JSX.Element}
 */
export default function PlanWorkspaceHeader({
  plan,
  saveState,
  validationState,
  amendmentState,
  currentUser,
  canSubmit,
  canProposeAmendment,
  canCreateTemplate,
  onSave,
  onSubmit,
  onSaveAs,
  onSaveAsTemplate,
  onProposeAmendment,
  onCancelAmendment,
}) {
  // WHY: Menu visibility is presentation-only state and must not alter any plan or governance state.
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // WHY: The menu root is tracked solely so presentation can dismiss on an outside interaction.
  const menuRootRef = useRef(null);

  // WHY: triggerRef tracks the More▾ button element so focus can be returned to it
  // when the menu closes via Escape or outside mousedown. This is the WCAG-required
  // pattern for disclosure widgets — focus must return to the control that opened
  // the menu so keyboard users are not stranded after dismissal.
  // (OI-S755-C08-MENU-FOCUS-01)
  const triggerRef = useRef(null);

  // WHY: More-menu dismissal on Escape and outside click is required keyboard/pointer behavior and has no domain consequence.
  useEffect(() => {
    if (!moreMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      // WHY: Outside interaction should dismiss transient navigation chrome without firing governance actions.
      // Focus is returned to the trigger button so keyboard users land on a known, operable element
      // rather than being stranded at the document body. (OI-S755-C08-MENU-FOCUS-01)
      if (
        menuRootRef.current &&
        !menuRootRef.current.contains(event.target)
      ) {
        setMoreMenuOpen(false);
        triggerRef.current?.focus();
      }
    };

    const handleKeyDown = (event) => {
      // WHY: Escape provides a standard keyboard exit from the transient More menu.
      // Focus is explicitly returned to the trigger button — the WCAG disclosure pattern
      // requires this so the keyboard user's position in the document is preserved.
      // (OI-S755-C08-MENU-FOCUS-01)
      if (event.key === 'Escape') {
        setMoreMenuOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [moreMenuOpen]);

  // WHY: Menu toggling is presentation-only and intentionally does not mutate plan state.
  const handleMoreToggle = () => {
    setMoreMenuOpen((open) => !open);
  };

  // WHY: Each selected menu action closes transient chrome before delegating the actual workflow to its owning callback.
  const handleMenuAction = (callback) => {
    setMoreMenuOpen(false);
    callback();
  };

  // WHY: The visible version label distinguishes the governing version from an amendment draft without deriving temporal state from workflow status.
  const versionLabel = amendmentState.inAmendmentMode
    ? `V${plan.version} → V${amendmentState.draftVersion} (draft)`
    : `V${plan.version}`;

  // WHY: planStatus is used only here to decide whether the Submit action belongs in the visible action set.
  const showSubmitAction =
    plan.planStatus === 'draft' ||
    plan.planStatus === 'pending_approval';

  return (
    <header
      style={styles.header}
      data-component="PlanWorkspaceHeader"
    >
      <div style={styles.primaryRow}>
        <div style={styles.identity}>
          <div style={styles.nameRow}>
            <span style={styles.name}>{plan.planName}</span>
            <span style={styles.code}>{plan.planCode}</span>
            <span style={styles.badge}>{versionLabel}</span>
          </div>

          <div style={styles.temporal}>
            Effective: {plan.effectiveFrom || 'Not set'} →{' '}
            {plan.effectiveTo || 'open-ended'}
          </div>

          {/* WHY: Amendment economics require a separately named effective date so it cannot be mistaken for workflow approval timing. */}
          {amendmentState.inAmendmentMode && (
            <div style={styles.temporal}>
              Amendment economic effective date:{' '}
              {amendmentState.amendmentEffectiveDate || 'Not set'}
            </div>
          )}

          <div style={styles.statuses}>
            {/* WHY: Autosave state is persistence health and is deliberately rendered separately from validation/governance state. */}
            {saveState.status === 'saving' && (
              <span>Saving…</span>
            )}

            {/* WHY: Successful persistence is surfaced independently of whether the document is valid or approvable. */}
            {saveState.status === 'saved' && (
              <span>
                Saved {formatRelativeSavedTime(saveState.lastSavedAt)}
              </span>
            )}

            {/* WHY: A save failure needs an explicit retry path but must not cause optimistic local document mutation. */}
            {saveState.status === 'error' && (
              <span className="modifier-manager-inline-error">
                {saveState.errorMessage || 'Save failed.'}{' '}
                <button
                  type="button"
                  className="text-button"
                  onClick={onSave}
                >
                  Retry
                </button>
              </span>
            )}

            {/* OI-BLOCKING-ISSUE-BADGE-NOT-CLICKABLE S1022:
                Blocking issue count was a plain <span> — CA could not click it to
                discover what the blocking issue was. Changed to a <button> wired to
                onSubmit, which opens SubmissionReadinessPanel (same as the Submit
                button). The panel lists blocking issues with section navigation links.
                WHY button not link: this is an action that triggers a panel, not
                navigation to a URL. WCAG: role is implicit from button element. */}
            {validationState.hasBlockingErrors && (
              <button
                type="button"
                className="modifier-manager-warning"
                style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                onClick={onSubmit}
                aria-label={`${validationState.blockingCount} blocking issue${validationState.blockingCount === 1 ? '' : 's'} — click to view details`}
              >
                {validationState.blockingCount} blocking issue
                {validationState.blockingCount === 1 ? '' : 's'}
              </button>
            )}

            {/* WHY: Non-blocking warnings remain visible even when no blocking issue exists so readiness is not reduced to a binary state. */}
            {validationState.warningCount > 0 && (
              <span>
                {validationState.warningCount} warning
                {validationState.warningCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>

        <div
          style={styles.actions}
          ref={menuRootRef}
        >
          <button
            type="button"
            className="secondary-button"
            onClick={onSave}
          >
            Save
          </button>

          {/* WHY: planStatus controls only whether the Submit action is visible; canSubmit and validation decide whether the supplied action is currently usable. */}
          {showSubmitAction && (
            <button
              type="button"
              className="primary-button"
              disabled={
                validationState.hasBlockingErrors ||
                !canSubmit
              }
              onClick={onSubmit}
            >
              Submit
            </button>
          )}

          {/* WHY: ref={triggerRef} captures this button element so focus can be
               returned to it when the menu closes. Without the ref, Escape and
               outside-mousedown dismissal strand the keyboard user at body focus.
               (OI-S755-C08-MENU-FOCUS-01) */}
          <button
            ref={triggerRef}
            type="button"
            className="secondary-button"
            aria-haspopup="menu"
            aria-expanded={moreMenuOpen}
            onClick={handleMoreToggle}
          >
            More ▾
          </button>

          {/* WHY: The menu is transient presentation state; action eligibility is supplied by parent contracts rather than inferred here. */}
          {moreMenuOpen && (
            <div
              style={styles.menu}
              role="menu"
              aria-label="Plan actions"
            >
              <button
                type="button"
                className="text-button"
                style={styles.menuButton}
                role="menuitem"
                onClick={() => handleMenuAction(onSaveAs)}
              >
                Save as new plan
              </button>

              {/* WHY: Certified-plan candidacy is supplied by governance context, so this component only controls visibility. */}
              {canCreateTemplate && (
                <button
                  type="button"
                  className="text-button"
                  style={styles.menuButton}
                  role="menuitem"
                  onClick={() => handleMenuAction(onSaveAsTemplate)}
                >
                  Create Certified Plan Candidate
                </button>
              )}

              {/* WHY: Amendment eligibility is supplied by the governance layer and is not recomputed from plan status here. */}
              {canProposeAmendment && (
                <button
                  type="button"
                  className="text-button"
                  style={styles.menuButton}
                  role="menuitem"
                  onClick={() => handleMenuAction(onProposeAmendment)}
                >
                  Propose amendment
                </button>
              )}

              {/* WHY: Cancellation is visible only while amendment-mode presentation is active; the actual cancellation workflow remains outside this header. */}
              {amendmentState.inAmendmentMode && (
                <button
                  type="button"
                  className="text-button text-button--danger"
                  style={styles.menuButton}
                  role="menuitem"
                  onClick={() => handleMenuAction(onCancelAmendment)}
                >
                  Cancel amendment
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* WHY: The approved-versus-proposed distinction must remain explicit for the full duration of amendment mode. */}
      {amendmentState.inAmendmentMode && (
        <div style={styles.amendmentRow}>
          Approved V{plan.version} remains governing — amendment V
          {amendmentState.draftVersion} pending
        </div>
      )}
    </header>
  );
}

PlanWorkspaceHeader.propTypes = {
  plan: PropTypes.shape({
    planId: PropTypes.number.isRequired,
    planCode: PropTypes.string.isRequired,
    planName: PropTypes.string.isRequired,
    version: PropTypes.number.isRequired,
    planStatus: PropTypes.string.isRequired,
    effectiveFrom: PropTypes.string,
    effectiveTo: PropTypes.string,
  }).isRequired,
  saveState: PropTypes.shape({
    status: PropTypes.oneOf(['idle', 'saving', 'saved', 'error']).isRequired,
    lastSavedAt: PropTypes.string,
    errorMessage: PropTypes.string,
  }).isRequired,
  validationState: PropTypes.shape({
    hasBlockingErrors: PropTypes.bool.isRequired,
    blockingCount: PropTypes.number.isRequired,
    warningCount: PropTypes.number.isRequired,
  }).isRequired,
  amendmentState: PropTypes.shape({
    inAmendmentMode: PropTypes.bool.isRequired,
    draftVersion: PropTypes.number,
    amendmentEffectiveDate: PropTypes.string,
  }).isRequired,
  currentUser: PropTypes.object.isRequired,
  canSubmit: PropTypes.bool.isRequired,
  canProposeAmendment: PropTypes.bool.isRequired,
  canCreateTemplate: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSaveAs: PropTypes.func.isRequired,
  onSaveAsTemplate: PropTypes.func.isRequired,
  onProposeAmendment: PropTypes.func.isRequired,
  onCancelAmendment: PropTypes.func.isRequired,
};

/**
 * @example
 * <PlanWorkspaceHeader
 *   plan={document.plan}
 *   saveState={planDoc.saveState}
 *   validationState={{
 *     hasBlockingErrors: validationResult.hasBlockingErrors,
 *     blockingCount: validationResult.blockingCount,
 *     warningCount: validationResult.warningCount,
 *   }}
 *   amendmentState={{
 *     inAmendmentMode: governance.inAmendmentMode,
 *     draftVersion: governance.amendment?.draftVersion ?? null,
 *     amendmentEffectiveDate: governance.amendment?.effectiveDate ?? null,
 *   }}
 *   currentUser={currentUser}
 *   canSubmit={governance.canSubmit}
 *   canProposeAmendment={governance.canProposeAmendment}
 *   canCreateTemplate={governance.canCreateTemplate}
 *   onSave={planDoc.onSave}
 *   onSubmit={() => setReadinessOpen(true)}
 *   onSaveAs={() => setSaveAsOpen(true)}
 *   onSaveAsTemplate={() => setTemplateOpen(true)}
 *   onProposeAmendment={() => setAmendmentOpen(true)}
 *   onCancelAmendment={() => setCancelOpen(true)}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - DP-1: Effective range is always rendered.
 * - INV-1: Amendment date is explicitly labeled "Amendment economic effective date".
 * - INV-3: planStatus participates only in Submit action visibility.
 * - DP-2: Amendment mode always includes the approved-version-governing statement.
 * - DP-10: All mutations are callback-driven; no optimistic domain state exists here.
 * - XC-5 intent: Save state and validation state are separate visual signals.
 * - OI-S755-C08-MENU-FOCUS-01: triggerRef on More▾ button; focus returned to trigger
 *   on Escape and outside-mousedown menu dismissal. useRef was imported in S755 but
 *   only exercised for menuRootRef; triggerRef was the missing second use.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 * - plan.effective_from — pending m_tc_01.
 * - plan.effective_to — pending m_tc_01.
 */

/**
 * ACCESSIBILITY NOTES
 * - Validation and save states are conveyed with readable text.
 * - More menu exposes aria-haspopup, aria-expanded, role="menu", and role="menuitem".
 * - Escape and outside-mousedown dismiss the transient More menu.
 * - On menu close (Escape or outside interaction), focus returns to the More▾ trigger
 *   button via triggerRef — WCAG disclosure widget pattern. (OI-S755-C08-MENU-FOCUS-01)
 * - All actions use native button elements.
 */